// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { usePersona } from '../../hooks/usePersona';
import { DISPLAY_LOCAL_ONLY } from '../../config/featureFlags';
import { LD_FLAGS } from '../../config/launchDarklyFlags';
import styles from './FeatureFlagDebugPanel.module.css';

/**
 * Live readout of every catalog flag (config/launchDarklyFlags.ts) — for
 * demos and local debugging, not a product surface. `useFlags()` already
 * re-renders on every flag change the SDK streams in, so flipping a flag in
 * the LaunchDarkly dashboard updates this panel with no refresh — that's
 * the whole point of it existing: watching a value change live is a more
 * convincing demo than being told it changed.
 *
 * Gated the same way DISPLAY_LOCAL_ONLY gates every other not-for-outside-
 * eyes surface: off by default, opt in locally via front/.env.local, and
 * admin-only on top of that. New catalog entries show up automatically —
 * nothing to wire per flag.
 *
 * The one sanctioned caller of the SDK's raw useFlags() outside
 * useFeatureFlag.ts: this panel enumerates every catalog entry's live value
 * at once, which a per-flag hook can't do without calling a hook in a loop.
 * Every other component should go through useFlagOn(name) or FlagRoute, not this.
 */
export default function FeatureFlagDebugPanel() {
  const { isAdmin } = usePersona();
  const flags = useFlags();

  if (!DISPLAY_LOCAL_ONLY || !isAdmin) return null;

  const names = Object.keys(LD_FLAGS) as Array<keyof typeof LD_FLAGS>;

  return (
    <div className={styles.panel} data-testid="feature-flag-debug-panel">
      <div className={styles.title}>Feature flags</div>
      {names.map(name => {
        const { key, default: fallback } = LD_FLAGS[name];
        const raw = flags[key];
        const value = typeof raw === 'boolean' ? raw : fallback;
        return (
          <div key={key} className={styles.row}>
            <span className={`${styles.state} ${value ? styles.on : styles.off}`}>
              {value ? 'ON' : 'OFF'}
            </span>
            <code>{key}</code>
          </div>
        );
      })}
    </div>
  );
}
